import { Button, Table } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Col, Container, Row } from 'reactstrap';

import { PathsLayouts } from '../../../../../config/routes';
import { useStudentCurrentAccountLoader } from '../../components/StudentCurrentAccountLoader';
import { listRefunds } from '../../Refunds/api';
import { Refund } from '../../Refunds/types';
import useColumns from '../hooks/useColumns';
import { Debt, DebtTableRow } from '../types';
import { useDebtLoader } from './DebtLoader';
import DebVoucherModal, { DebVoucherModalRef } from './DebtVoucherModal';

const keyPrefix = 'financialPortal.currentAccount.tabs.currentAccount';

interface DebtTableProps {
  /**
   * Cuando cambia, fuerza un refetch de devoluciones (HU-15411). El padre lo
   * incrementa después de crear/aplicar una devolución para reflejar el nuevo
   * estado en la misma tabla de Cuenta corriente sin recargar la vista.
   */
  refundsRefreshToken?: number;
}

function DebtTable({ refundsRefreshToken }: DebtTableProps) {
  const { t } = useTranslation();
  const history = useHistory();

  const { data: studentData } = useStudentCurrentAccountLoader();
  const studentId = studentData!.studentId;

  const [selectedDebts, setSelectedDebts] = useState<Debt[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [refundsLoading, setRefundsLoading] = useState<boolean>(false);

  const { data, loading } = useDebtLoader();
  const debtVoucherModalRef = useRef<DebVoucherModalRef>(null);

  useEffect(() => {
    let mounted = true;
    setRefundsLoading(true);
    listRefunds(studentId).then(({ data: list }) => {
      if (!mounted) return;
      setRefunds(list ?? []);
      setRefundsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [studentId, refundsRefreshToken]);

  const onClickDetail = useCallback(
    (row: Debt) => {
      history.push(
        `${PathsLayouts.currentAccount}/${studentId}/debt-detail/${row.id}`,
      );
    },
    [history, studentId],
  );

  const onClickApplyRefund = useCallback(
    (refundId: string) => {
      history.push(
        `${PathsLayouts.currentAccount}/${studentId}/refunds/${refundId}/apply`,
      );
    },
    [history, studentId],
  );

  const onClickNotifySelectedDebts = useCallback((row: Debt) => {
    setSelectedDebts((prev) => {
      const exists = prev.some((d) => d.id === row.id);
      return exists ? prev.filter((d) => d.id !== row.id) : [...prev, row];
    });
  }, []);

  const columns = useColumns({
    onClickDetail,
    onClickApplyRefund,
    selectedDebts,
    onClickNotifySelectedDebts,
  });

  const rows = useMemo<DebtTableRow[]>(() => {
    const debtRows: DebtTableRow[] = (data?.debts ?? []).map((d) => ({
      __kind: 'debt' as const,
      ...d,
    }));
    const pendingRefunds = refunds.filter((r) => Number(r.amount) > 0);
    const refundRows: DebtTableRow[] = pendingRefunds.map((r) => ({
      __kind: 'refund' as const,
      id: r.id,
      refund: r,
    }));
    return [...debtRows, ...refundRows];
  }, [data?.debts, refunds]);

  return (
    <>
      <div className="divider my-4" />
      <Table
        columns={columns}
        data={rows}
        isLoadingResults={loading || refundsLoading}
        noResultsText={
          <div className="text-center py-4">
            <p className="fs-18 fw-600 mb-1">
              {t(`${keyPrefix}.emptyTable.title`)}
            </p>
            <p className="text-muted mb-0">
              {t(`${keyPrefix}.emptyTable.body`)}
            </p>
          </div>
        }
      />
      <Container fluid className="mt-5">
        <Row>
          <Col xs={12} md={{ size: 6, offset: 6 }}>
            <Row>
              <Col xs={12} md={6}>
                <Button
                  type="button"
                  text={t(`${keyPrefix}.rescheduleDebts`)}
                  loading={loading}
                  size="md"
                  fullwidth
                  outlined
                  onClick={async () => {}}
                />
              </Col>
              <Col xs={12} md={6}>
                <Button
                  type="button"
                  onClick={() =>
                    debtVoucherModalRef.current?.openModal(selectedDebts)
                  }
                  text={t(`${keyPrefix}.generateVoucher`)}
                  size="md"
                  disabled={loading || selectedDebts.length === 0}
                  fullwidth
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      <DebVoucherModal
        ref={debtVoucherModalRef}
        onConfirm={() => {}}
        onDecline={() => {}}
      />
    </>
  );
}

export default DebtTable;
